import { cloneDeep } from "lodash";
import moment from "moment";
import i18nService from "@/services/i18n";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { reportDkiPFilterOptions } from "../report-dki-p";
import { ReportDkiPFilterSettingsForm } from "../settings-form/ReporDkiPFilterSettingsForm";
import { ReportDkiPFilterSettings } from "../settings/ReportDkiPFilterSettings";
import { ReportDkiPTableDataRequest } from "../data-request/ReportDkiPTableDataRequest";
import { ReportDkiPJournalRequest } from "../journal/ReportDkiPJournalRequest";
import { ReportDkiPTableData } from "../table-data/ReportDkiPTableData";
import { allIncomeClassificators } from "../../../common/baseOptions";

export class ReportDkiPFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(reportDkiPFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидаций
        if (settings.reportName !== 'DKI-P') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== 'DKI-P') {
            throw new Error('error_type_filter_options')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }

        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        dataSource = { ...dataSource }

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        periodicity = { ...periodicity }

        let regionOption = options.regions.find(it => it.value === settings.region)
        if (!regionOption) {
            throw new Error('error_not_found_region_option')
        }
        regionOption = { ...regionOption }

        let mutuallyRedeemingOption = options.mutuallyRedeeming.find(it => it.value === settings.mutuallyRedeeming)
        if (!mutuallyRedeemingOption) {
            throw new Error('error_not_found_mutually_redeeming_option')
        }
        mutuallyRedeemingOption = { ...mutuallyRedeemingOption }

        let measureUnitOption = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnitOption) {
            throw new Error('error_not_found_measure_unit')
        }
        measureUnitOption = { ...measureUnitOption }

        const budgetTypeOptions = cloneDeep(
            options.budgetTypes.filter(it => settings.budgetTypes.includes(it.value))
        )

        let isRoundUp: boolean = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }

        // 2. Формирование
        const form: ReportDkiPFilterSettingsForm = {
            // Base:
            key: 'DKI-P',
            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,
            // Custom:
            region: regionOption,
            date: new Date(settings.date),
            budgetTypes: budgetTypeOptions,
            mutuallyRedeeming: mutuallyRedeemingOption,
            measureUnit: measureUnitOption,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo ?? null,
            incomes: cloneDeep(settings.incomes),
            incomeTotal: settings.incomeTotal
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'DKI-P') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (form.region == null) {
            throw new Error('error_input_region')
        }
        if (!form.date) {
            throw new Error('error_input_date')
        }
        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }
        if (!form.mutuallyRedeeming) {
            throw new Error('error_input_mutually_redeeming')
        }
        
        const strDate = moment(form.date).format('YYYY-MM-DD')

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const settings: ReportDkiPFilterSettings = {
            // Base:
            reportName: 'DKI-P',
            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,
            // Custom:
            region: form.region.value,
            date: strDate,
            budgetTypes: form.budgetTypes.map(it => it.value),
            measureUnit: form.measureUnit.value,
            roundUpTo: roundUpTo,
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            incomes: cloneDeep(form.incomes),
            incomeTotal: form.incomeTotal
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(reportDkiPFilterOptions)

        const yesterdayMoment = moment().subtract(1, 'days')
        const yesterdayDate = yesterdayMoment.toDate()

        const emptyForm: ReportDkiPFilterSettingsForm = {
            key: 'DKI-P',
            // Base fields
            reportName: options.reportNames.find(it => it.value === 'DKI-P')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'INCOMES')!,
            dataSource: options.dataSources.find(it => it.value === 'LOADER')!,
            periodicity: options.periodicities.find(it => it.value === 'DAY')!,
            // Custom fields            
            region: null,
            date: yesterdayDate,
            budgetTypes: [],
            mutuallyRedeeming: null,
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            roundUp: false,
            roundUpTo: null,
            incomes: cloneDeep(allIncomeClassificators).map(it => {it.active = true; return it}),
            incomeTotal: true
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций и Формирование данных для таблицы
        if (form.key !== 'DKI-P') {
            throw new Error('error_type_filter_settingsForm')
        }
        
        if (!form.date) {
            throw new Error('error_input_date')
        }
        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }
        if (!form.region) {
            throw new Error('error_input_region')
        }
        if (!form.mutuallyRedeeming) {
            throw new Error('error_input_mutually_redeeming')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo
        }

        const strDate = moment(form.date).format('YYYY-MM-DD')

        const incomeParams = form.incomes
            .map(it => ({ key: it.key, selected: it.selected, active: it.active }))

        const params: ReportDkiPTableDataRequest = {
            report_date: strDate,
            region: form.region.value,
            budget_types: form.budgetTypes.map(it => it.value),
            measure_unit: form.measureUnit.value,
            round_up_to: roundUpTo,
            mutually_redeeming: form.mutuallyRedeeming.value,
            incomes: incomeParams
        }

        const response = await fetch('/api-py/monitoring/reports-constructor/dki-p',
            {
                method: 'POST',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }
        const responseData = await response.json()
        const tableData: ReportDkiPTableData = responseData

        // 2. Формирование параметров для выгрузки журнал
        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const journalParams: ReportDkiPJournalRequest = {
            reportName: "DKI-P", // Приходится задавать для поддержки generics
            // journal data
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            // for excel
            incomes: incomeParams.map(it => it.key),
            report_date: strDate,
            region: form.region.value,
            budget_types: form.budgetTypes.map(it => it.value),
            measure_unit: form.measureUnit.value,
            income_total: form.incomeTotal,
            items: tableData.items,
            total: tableData.total,
            locale: i18nService.locale,
        }

        const result: TableDataAndJournalParams = {
            tableData: tableData,
            journalParams: journalParams
        }

        return result
    }
}
